/* jshint esnext: true */
import { Controller } from '@hotwired/stimulus';

// Toggles thousand separators (commas) on/off across the LIAS tool so the
// numbers can be transcribed into e-Gov (which rejects commas) while still
// defaulting to comma-separated display for users who hand-write on paper.
//
// Display-only: it never changes the underlying calculated/stored values,
// it only rewrites how numbers are shown. Form input values still carry the
// raw digits when commas are hidden, which is what e-Gov copy-paste needs.
export default class extends Controller {
  static targets = ['toggle'];

  connect() {
    // The amount form fields are (re)written with commas by the legacy jQuery
    // recalculation, which is delegated on `document` and therefore runs after
    // this controller's bubbling-phase listener. Defer to a macrotask so we
    // strip commas last and the comma-free view is not silently restored.
    this.boundApply = () => setTimeout(() => this.apply(), 0);

    this.element.addEventListener('keyup', this.boundApply);
    this.element.addEventListener('change', this.boundApply);
    this.element.addEventListener('input', this.boundApply);
    this.addCopyButtons();
    this.apply();
  }

  disconnect() {
    this.element.removeEventListener('keyup', this.boundApply);
    this.element.removeEventListener('change', this.boundApply);
    this.element.removeEventListener('input', this.boundApply);
  }

  apply() {
    const commaFree = this.hasToggleTarget && this.toggleTarget.checked;

    this.element.classList.toggle('lias-comma-free', commaFree);
    if (commaFree) this.stripCommas();
  }

  stripCommas() {
    // Read-only / computed amount fields rendered as inputs.
    this.element.querySelectorAll('input[type=text]').forEach((input) => {
      const stripped = this.withoutCommas(input.value);

      if (input.value !== stripped) input.value = stripped;
    });

    // Aggregated amounts rendered as plain text via number_with_delimiter.
    this.element.querySelectorAll('[data-lias-comma-target="amount"]').forEach((node) => {
      const stripped = this.withoutCommas(node.textContent);

      if (node.textContent !== stripped) node.textContent = stripped;
    });
  }

  withoutCommas(value) {
    if (value == null) return value;

    return value.toString().replace(/,/g, '');
  }

  // Inject a small copy-icon button after each computed (readonly) amount field
  // so a user can one-click copy the comma-free value for e-Gov transcription,
  // regardless of the comma toggle state (#1785 follow-up).
  addCopyButtons() {
    this.element
      .querySelectorAll('input[type=text][readonly]')
      .forEach((input) => this.addCopyButton(input));
  }

  addCopyButton(input) {
    // Skip if already wrapped (controller re-connect / hot reload safety).
    if (input.dataset.liasCopyReady === '1') return;

    input.dataset.liasCopyReady = '1';

    const button = document.createElement('button');

    button.type = 'button';
    button.className = 'btn btn-sm btn-outline-secondary lias-copy-btn';
    button.setAttribute('aria-label', 'コピーする');
    button.innerHTML = '<span class="fa fa-copy" aria-hidden="true"></span>';
    button.addEventListener('click', () => this.copyValue(input, button));
    this.addTooltip(button, 'コピーする');

    // Group the field and the button so they sit side by side.
    const group = document.createElement('div');

    group.className = 'lias-copy-group';
    input.parentNode.insertBefore(group, input);
    group.appendChild(input);
    group.appendChild(button);
  }

  // ホバー時に「コピーする」ツールチップを表示する。動的生成ボタンのため、
  // グローバル初期化(main.js)では拾えないので個別に Bootstrap5 の
  // Tooltip を生成する。bootstrap 未ロード環境ではネイティブの title で代替する。
  addTooltip(button, text) {
    button.title = text;
    const bs = window.bootstrap;
    if (bs && bs.Tooltip) {
      button.setAttribute('data-bs-toggle', 'tooltip');
      bs.Tooltip.getOrCreateInstance(button);
    }
  }

  copyValue(input, button) {
    const text = this.withoutCommas(input.value);

    const done = () => this.flashCopied(button);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, () => this.fallbackCopy(input, done));
    } else {
      this.fallbackCopy(input, done);
    }
  }

  fallbackCopy(input, done) {
    const previous = input.value;

    input.value = this.withoutCommas(input.value);
    input.select();
    try {
      document.execCommand('copy');
    } catch (e) {
      // Ignore — clipboard may be unavailable in the environment.
    }
    input.value = previous;
    done();
  }

  flashCopied(button) {
    button.classList.add('lias-copy-btn--copied');
    clearTimeout(button.dataset.liasCopyTimer);
    button.dataset.liasCopyTimer = setTimeout(() => {
      button.classList.remove('lias-copy-btn--copied');
    }, 1200);
  }
}
